import type { FC } from 'react';
import styled, { css } from 'styled-components';
import type { Layout } from 'react-grid-layout';
import { rgba } from 'polished';

import Grid from '../../Grid';
import type { NoChildrenProp } from '../../../types';
import { defaultThemeProp } from '../../../theme';
import { useI18n } from '../../../hooks';

import useGridLayoutConfig from './config';
import { getArrayFromRange } from './utils';

const StyledGrid = styled(Grid)`
  position: absolute;
  z-index: -1;
  pointer-events: none;
  inline-size: 100%;
`;

const StyledGridCell = styled.div<{ rowHeight: number; highlighted: boolean }>(
  ({ theme, highlighted, rowHeight }) => {
    const {
      base: { palette }
    } = theme;
    const borderColor = palette[highlighted ? 'warn' : 'border-line'];
    const background = highlighted ? rgba(palette.warn, 0.1) : 'transparent';

    return css`
      /* stylelint-disable-next-line unit-allowed-list */
      border: 1px solid ${borderColor};
      background: ${background};
      block-size: ${rowHeight}px;
      border-radius: ${theme.components.card['border-radius']};
    `;
  }
);
StyledGridCell.defaultProps = defaultThemeProp;

interface PageBreakProps {
  rowsAbove: number;
  rowHeight: number;
  margin: [number, number];
  containerPadding: [number, number];
  prefix: string;
}

const StyledPageBreak = styled.div<PageBreakProps>(
  ({ containerPadding, rowHeight, theme, margin, rowsAbove, prefix }) => {
    const {
      base: { palette }
    } = theme;
    return css`
      position: absolute;
      inset-inline-start: ${containerPadding[0]}px;
      inset-inline-end: ${containerPadding[0]}px;
      inset-block-start: ${containerPadding[1] + rowsAbove * (rowHeight + margin[1])}px;
      /* stylelint-disable-next-line unit-allowed-list */
      margin-block-start: ${-margin[1] / 2 - 1}px;
      /* stylelint-disable-next-line unit-allowed-list */
      border-block-start: 2px dashed ${palette['border-line']};

      &::before {
        content: '${prefix}';
        position: absolute;
        font-size: 0.75rem;
        line-height: 1;
        transform: translate(0, -50%);
        background-color: ${palette['app-background']};
        color: ${palette['border-line']};
        padding-inline-end: 0.25rem;
      }
    `;
  }
);
StyledPageBreak.defaultProps = defaultThemeProp;

interface EditorBackgroundProps extends NoChildrenProp {
  layout?: Layout[];
  numColumns: number;
  rowHeight: number;
  rowsPerPage: number | null; // null when not applying auto-fit
  highlighted: boolean;
}

/**
 * Renders background grid-cells for GridEditor
 * @component
 */
const EditorBackground: FC<EditorBackgroundProps> = ({
  layout,
  numColumns,
  rowHeight,
  rowsPerPage,
  highlighted
}) => {
  const t = useI18n();
  const { margin, containerPadding } = useGridLayoutConfig(true);

  const maxYInLayout = layout ? Math.max(...layout.map(item => item.y + item.h)) : 0;
  const maxY = Math.max(maxYInLayout, rowsPerPage || 0);
  const numGridCells = (maxY + 1) * numColumns;
  const cells = getArrayFromRange(0, numGridCells, false);

  // rowsPerPage will be null if auto-fit is disabled. Don't show pageBreaks in that case.
  const numPageBreaks = rowsPerPage ? Math.floor(maxY / rowsPerPage) : 0;
  const pageBreaks = getArrayFromRange(0, numPageBreaks, false);

  return (
    <StyledGrid container={{ cols: `repeat(${numColumns}, 1fr)`, gap: 2, pad: [0, 2, 2, 2] }}>
      {cells.map(n => (
        <StyledGridCell
          key={n}
          rowHeight={rowHeight}
          // Highlight all cells except the dummy row at the bottom
          highlighted={highlighted && n < numGridCells - numColumns}
        />
      ))}
      {pageBreaks.map(n => (
        <StyledPageBreak
          key={n}
          rowsAbove={(n + 1) * rowsPerPage!}
          rowHeight={rowHeight}
          margin={margin}
          containerPadding={containerPadding}
          prefix={t('page_number', [n + 1])}
        />
      ))}
    </StyledGrid>
  );
};

export default EditorBackground;
